import React, { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Floating WhatsApp support button. Mirrors the customer-frontend
 * `DraggableWhatsApp` FAB shape — circular icon-only target instead of
 * the older pill-with-label so the FAB stops blocking dialog content.
 *
 * Behaviour:
 *  - Drag to reposition (pointer-down + move).
 *  - Tap (no drag) opens wa.me in a new tab so the merchant page stays
 *    in the original tab.
 *  - z-30 sits below any open Dialog (z-9999) and any inline modal
 *    overlay (Tailwind z-50) so the button never covers their content.
 */
const SupportButton: React.FC = () => {
  const whatsappNumber = process.env.REACT_APP_WHATSAPP_NUMBER || '6281234567890';

  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, startPosX: 0, startPosY: 0, moved: false });
  const elRef = useRef<HTMLDivElement>(null);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    setDragging(true);
    dragRef.current = { startX: e.clientX, startY: e.clientY, startPosX: pos.x, startPosY: pos.y, moved: false };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [pos]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.moved = true;
    setPos({ x: dragRef.current.startPosX + dx, y: dragRef.current.startPosY + dy });
  }, [dragging]);

  const handlePointerUp = useCallback(() => {
    setDragging(false);
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (dragRef.current.moved) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  // Keep the FAB inside the viewport when the user resizes the window.
  useEffect(() => {
    const constrain = () => {
      if (!elRef.current) return;
      const rect = elRef.current.getBoundingClientRect();
      const maxX = window.innerWidth - rect.width - 24;
      const maxY = window.innerHeight - rect.height - 24;
      setPos((prev) => ({
        x: Math.min(Math.max(prev.x, -maxX), 0),
        y: Math.min(Math.max(prev.y, -maxY), 0),
      }));
    };
    window.addEventListener('resize', constrain);
    return () => window.removeEventListener('resize', constrain);
  }, []);

  const message = encodeURIComponent('Hi, I need help with my order');
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`;

  return (
    <div
      ref={elRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      style={{ transform: `translate(${pos.x}px, ${pos.y}px)`, touchAction: 'none' }}
      className="fixed bottom-6 right-6 z-30 select-none"
    >
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        className="w-10 h-10 md:w-12 md:h-12 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-lg transition-colors"
        aria-label="Chat on WhatsApp"
      >
        <svg viewBox="0 0 32 32" className="w-4 h-4 md:w-6 md:h-6 fill-white">
          <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.128 6.744 3.046 9.378L1.054 31.29l6.118-1.958A15.9 15.9 0 0016.004 32C24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.308 22.586c-.388 1.094-1.938 2.002-3.172 2.266-.846.18-1.95.322-5.67-1.218-4.762-1.97-7.826-6.81-8.064-7.124-.23-.314-1.924-2.562-1.924-4.888 0-2.326 1.218-3.47 1.65-3.944.388-.424 1.024-.618 1.634-.618.196 0 .374.01.532.018.472.02.71.048 1.022.788.388.926 1.336 3.252 1.452 3.49.118.238.236.554.078.868-.15.322-.282.466-.52.736-.238.27-.464.476-.702.766-.22.254-.468.526-.194.998.274.464 1.218 2.008 2.616 3.254 1.796 1.598 3.306 2.094 3.776 2.324.472.23.748.194 1.022-.118.282-.314 1.2-1.398 1.52-1.878.312-.48.632-.396 1.064-.238.436.15 2.76 1.302 3.232 1.538.472.238.788.354.906.55.116.194.116 1.134-.272 2.228z" />
        </svg>
      </a>
    </div>
  );
};

export default SupportButton;
